/** Literals the Strategies screen owns — ROADMAP 5.19. */

import type { PremiumFlow, RiskShape } from './utils/strategyFacts'

/** Display order, loosest risk posture first. Not alphabetical. */
export const BIAS_ORDER = ['NEUTRAL', 'BULLISH', 'BEARISH', 'VOLATILITY', 'SPECIAL'] as const

/** Sentence case for a pill; the section headings uppercase it themselves. */
export const BIAS_LABEL: Record<string, string> = {
  NEUTRAL: 'Neutral',
  BULLISH: 'Bullish',
  BEARISH: 'Bearish',
  VOLATILITY: 'Volatility',
  SPECIAL: 'Special',
}

/** Text colour for a bias, used on the heading and the card's own tag. */
export const BIAS_CLASS: Record<string, string> = {
  NEUTRAL: 'text-muted-foreground',
  BULLISH: 'text-success',
  BEARISH: 'text-destructive',
  VOLATILITY: 'text-primary',
  SPECIAL: 'text-warning',
}

/**
 * The card's own bias tag — a tinted pill, matching the leg chips.
 *
 * Tinted rather than outlined because the tag sits beside a strategy NAME
 * the trader may have rewritten ("Monday condor"), and the bias is then the
 * only thing on the card still saying which way the structure leans.
 */
export const BIAS_TAG: Record<string, string> = {
  NEUTRAL: 'bg-muted text-muted-foreground',
  BULLISH: 'bg-success/10 text-success',
  BEARISH: 'bg-destructive/10 text-destructive',
  VOLATILITY: 'bg-primary/10 text-primary',
  SPECIAL: 'bg-warning/15 text-warning',
}

/** The 3px rail down a card's left edge. Identity at a glance, while scrolling. */
export const BIAS_RAIL: Record<string, string> = {
  NEUTRAL: 'bg-muted-foreground/40',
  BULLISH: 'bg-success',
  BEARISH: 'bg-destructive',
  VOLATILITY: 'bg-primary',
  SPECIAL: 'bg-warning',
}

export const EXPIRY_LABEL: Record<string, string> = {
  WEEKLY: 'This week',
  NEXT_WEEKLY: 'Next week',
  MONTHLY: 'This month',
  NEXT_MONTHLY: 'Next month',
}

/**
 * How a tail reads on the card.
 *
 * NEVER COLOUR ALONE. An open tail is the one thing on this page a trader
 * must not miss, so it is a word first — the tint only reinforces it, and
 * the card survives a greyscale print or a colour-blind reader intact.
 */
export const RISK_LABEL: Record<RiskShape, string> = {
  DEFINED: 'Defined',
  OPEN_ABOVE: 'Open above',
  OPEN_BELOW: 'Open below',
  OPEN_BOTH: 'Open both ways',
}

export const RISK_CLASS: Record<RiskShape, string> = {
  DEFINED: 'text-foreground',
  OPEN_ABOVE: 'text-warning',
  OPEN_BELOW: 'text-warning',
  OPEN_BOTH: 'text-destructive',
}

/** Why the card says what it says, on hover. The teaching is in the tooltip. */
export const RISK_MEANING: Record<RiskShape, string> = {
  DEFINED:
    'Every leg is covered at both extremes, so the worst case at expiry is a finite number. Defined is not the same as small — size still decides what it costs.',
  OPEN_ABOVE:
    'Net short calls. Above the highest strike the loss keeps growing with spot, with nothing to cap it.',
  OPEN_BELOW:
    'Net short puts. Below the lowest strike the loss keeps growing as spot falls, bounded only by spot reaching zero.',
  OPEN_BOTH:
    'Net short on both sides. The loss keeps growing in either direction away from the strikes.',
}

/**
 * Credit or debit, as the card words it.
 *
 * NEITHER IS COLOURED. Green for a credit would read as "good", and a credit
 * is exactly where the open tails live — the two most dangerous structures in
 * the catalogue, a short straddle and a short strangle, both collect. Colour
 * on this page is reserved for the risk field, which is the one a trader must
 * not scan past.
 */
export const PREMIUM_LABEL: Record<PremiumFlow, string> = {
  CREDIT: 'Credit',
  DEBIT: 'Debit',
  DEPENDS: 'Depends',
}

export const PREMIUM_MEANING: Record<PremiumFlow, string> = {
  CREDIT:
    'The structure collects premium on entry. Provable from the strike ladder alone: a call is worth less the higher its strike, a put less the lower, so the ordering fixes the sign of the net whatever the prices are.',
  DEBIT:
    'The structure pays premium on entry. Provable from the strike ladder alone, by the same argument as a credit.',
  DEPENDS:
    'Not provable from the template. The legs are arranged so that whether it collects or pays turns on the actual prices — a ratio spread sells more contracts than it buys, and whether the cheap ones outsell the dear one is a fact about today\u2019s skew.',
}

/**
 * Leg chips rendered before the rest collapse into a "+N".
 *
 * A template may legitimately carry thirty legs, and thirty chips turn one
 * card into a page. Six is what fits two lines at the narrowest column the
 * grid produces.
 */
export const LEG_CHIP_LIMIT = 6
